import React, { useCallback, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useLang } from "@/hooks/useLang";
import { UI, trackEvent, trackAction } from "@/content/site";
import { useQuery } from "@tanstack/react-query";
import { fetchContent } from "@/lib/api";

/**
 * Slim banner on EN pages inviting Spanish speakers to switch, and vice versa.
 * Shown once per session, dismissable.
 */

const DISMISSED_KEY = "gw-lang-banner-dismissed";

function readDismissed() {
  try {
    return sessionStorage.getItem(DISMISSED_KEY) === "1";
  } catch (err) {
    // sessionStorage may be blocked (private browsing / SSR / iframe).
    if (typeof console !== "undefined" && console.warn) {
      console.warn("[LanguageBanner] sessionStorage read failed:", err);
    }
    return false;
  }
}

function writeDismissed() {
  try {
    sessionStorage.setItem(DISMISSED_KEY, "1");
  } catch (err) {
    if (typeof console !== "undefined" && console.warn) {
      console.warn("[LanguageBanner] sessionStorage write failed:", err);
    }
  }
}

export default function LanguageBanner() {
  const lang = useLang();
  const { pathname } = useLocation();
  const [dismissed, setDismissed] = useState(true);

  // Determine hreflang pair for current path
  const { data } = useQuery({
    queryKey: ["content", pathname],
    queryFn: () => fetchContent(pathname).catch(() => null),
    staleTime: Infinity,
  });

  useEffect(() => {
    setDismissed(readDismissed());
  }, [lang]);

  const handleSwitch = useCallback(() => {
    writeDismissed();
    trackAction("language_switch", { source: "banner", from: lang });
  }, [lang]);

  const handleClose = useCallback(() => {
    writeDismissed();
    setDismissed(true);
  }, []);

  if (dismissed) return null;
  const pairUrl = data?.pair;
  if (!pairUrl) return null;
  const ui = UI[lang];

  return (
    <div className="gw-lang-banner" role="complementary">
      <span>{ui.preferSpanish}</span>
      <div>
        <Link
          to={pairUrl}
          className="gw-lang-banner-cta"
          onClick={handleSwitch}
        >
          {lang === "en" ? "Ver en español" : "View in English"}
        </Link>
        <button
          type="button"
          className="gw-lang-banner-close"
          aria-label="Close"
          onClick={handleClose}
        >
          ×
        </button>
      </div>
      <style>{`
        .gw-lang-banner {
          display: flex; align-items: center; justify-content: center; gap: 12px;
          padding: 10px 16px;
          background: var(--gw-primary-dark); color: #FFF9E8;
          font-size: 0.9rem;
          flex-wrap: wrap;
        }
        .gw-lang-banner-cta {
          display: inline-block; padding: 6px 14px;
          background: var(--gw-accent); color: var(--gw-primary-dark);
          border-radius: 999px; font-weight: 600; text-decoration: none;
        }
        .gw-lang-banner-cta:hover { color: var(--gw-primary-dark); text-decoration: none; background: var(--gw-accent-soft); }
        .gw-lang-banner-close {
          background: transparent; color: #fff; border: 0; font-size: 1.4rem;
          line-height: 1; padding: 4px 8px; cursor: pointer; margin-left: 8px;
        }
      `}</style>
    </div>
  );
}
